import { COLLIDERS, SHIP_COLLIDERS, overDeck, segmentColliderEntry, terrainHeight, worldToLocal, type Pose, type Vec3 } from "@tiny-seas/shared";

/** Chase camera: metres behind and above the eye, wall margin, closest pull-in. */
export const THIRD_PERSON = { distance: 3.2, height: 0.35, margin: 0.2, minDistance: 0.6 } as const;

const end: Vec3 = { x: 0, y: 0, z: 0 };
const localA: Vec3 = { x: 0, y: 0, z: 0 };
const localB: Vec3 = { x: 0, y: 0, z: 0 };
const TERRAIN_STEP = 0.25;
const TERRAIN_CLEARANCE = 0.3;
/** Deck-footprint sampling step; rendered bulwarks and the stern rise above the rail colliders. */
const DECK_STEP = 0.1;

/**
 * Boom length from `origin` along unit `back`, pulled in before the first static
 * world solid, ship hull/fixture or terrain so the chase camera never sees
 * through them.
 */
export function thirdPersonDistance(origin: Readonly<Vec3>, back: Readonly<Vec3>, ships: readonly (Pose | null)[]): number {
  const full = THIRD_PERSON.distance;
  end.x = origin.x + back.x * full; end.y = origin.y + back.y * full; end.z = origin.z + back.z * full;
  let t = 1;
  for (const collider of COLLIDERS) t = Math.min(t, segmentColliderEntry(origin, end, collider));
  for (const ship of ships) {
    if (!ship) continue;
    worldToLocal(ship, origin.x, origin.y, origin.z, localA);
    worldToLocal(ship, end.x, end.y, end.z, localB);
    for (const collider of SHIP_COLLIDERS) t = Math.min(t, segmentColliderEntry(localA, localB, collider));
    if (!overDeck(localA.x, localA.z)) continue;
    // Aboard, the boom never leaves the deck outline into the hull's rendered sides.
    const steps = Math.ceil(full / DECK_STEP);
    for (let i = 1; i <= steps; i++) {
      const s = i / steps;
      if (s >= t) break;
      if (!overDeck(localA.x + (localB.x - localA.x) * s, localA.z + (localB.z - localA.z) * s)) { t = (i - 1) / steps; break; }
    }
  }
  const samples = Math.ceil(full * t / TERRAIN_STEP);
  for (let i = 1; i <= samples; i++) {
    const s = Math.min(t, i * TERRAIN_STEP / full);
    const x = origin.x + back.x * full * s, y = origin.y + back.y * full * s, z = origin.z + back.z * full * s;
    if (y < terrainHeight(x, z) + TERRAIN_CLEARANCE) { t = Math.max(0, (i - 1) * TERRAIN_STEP / full); break; }
  }
  return t >= 1 ? full : Math.max(THIRD_PERSON.minDistance, t * full - THIRD_PERSON.margin);
}
